import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Navbar, Footer } from "../components/Layout";
import { VideoPlayer } from "../components/VideoPlayer";
import { Calendar, ArrowLeft, ArrowUpRight, Share2, List } from "lucide-react";
import DOMPurify from "dompurify";

// DOMPurify config: only allow safe inline tags produced by our markdown renderer
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["strong", "em", "a", "code", "br"],
  ALLOWED_ATTR: ["href", "class", "target", "rel"],
};
const sanitize = (html) => ({ __html: DOMPurify.sanitize(html, SANITIZE_CONFIG) });

// Lightweight markdown renderer (## h2, ### h3, **bold**, *italic*, - lists, links, paragraphs)
function renderMarkdown(text) {
  if (!text) return [];
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const l = lines[i];
    if (l.startsWith("## ")) {
      const title = l.slice(3).trim();
      const id = title.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      blocks.push({ type: "h2", text: title, id }); i++;
    } else if (l.startsWith("### ")) {
      blocks.push({ type: "h3", text: l.slice(4).trim() }); i++;
    } else if (l.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].startsWith("- ")) { items.push(lines[i].slice(2)); i++; }
      blocks.push({ type: "ul", items });
    } else if (l.trim() === "") {
      i++;
    } else {
      const paras = [l];
      i++;
      while (i < lines.length && lines[i].trim() !== "" && !lines[i].startsWith("##") && !lines[i].startsWith("- ")) {
        paras.push(lines[i]); i++;
      }
      blocks.push({ type: "p", text: paras.join(" ") });
    }
  }
  return blocks;
}

function inline(text) {
  // bold **x**, italic *x*, links [text](url)
  let html = text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="underline" rel="noopener noreferrer">$1</a>');
  return sanitize(html);
}

function injectMeta(post) {
  const url = `${window.location.origin}/blog/${post.slug}`;
  const title = post.seo_title || post.title;
  const desc = post.seo_description || post.excerpt;
  document.title = title;
  window.__pageTitleOverride = title;
  const setMeta = (selector, value) => {
    let el = document.head.querySelector(selector);
    if (!el) {
      el = document.createElement("meta");
      const m = selector.match(/\[(\w+)="([^"]+)"\]/);
      if (m) el.setAttribute(m[1], m[2]);
      document.head.appendChild(el);
    }
    el.setAttribute("content", value || "");
  };
  setMeta('meta[name="description"]', desc);
  setMeta('meta[property="og:type"]', "article");
  setMeta('meta[property="og:title"]', title);
  setMeta('meta[property="og:description"]', desc);
  setMeta('meta[property="og:url"]', url);
  setMeta('meta[property="og:image"]', post.cover_image_url || "");
  setMeta('meta[name="twitter:card"]', "summary_large_image");
  setMeta('meta[name="twitter:title"]', title);
  setMeta('meta[name="twitter:description"]', desc);
  setMeta('meta[name="twitter:image"]', post.cover_image_url || "");

  // JSON-LD schema.org BlogPosting
  const existing = document.getElementById("blog-jsonld");
  if (existing) existing.remove();
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = "blog-jsonld";
  script.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": desc,
    "image": post.cover_image_url ? [post.cover_image_url] : [],
    "author": { "@type": "Person", "name": post.author || "Administrateur" },
    "publisher": { "@type": "Organization", "name": "Immobilier Solaire" },
    "datePublished": post.created_at,
    "dateModified": post.updated_at,
    "mainEntityOfPage": { "@type": "WebPage", "@id": url },
  });
  document.head.appendChild(script);
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`).then(r => { setPost(r.data); injectMeta(r.data); }).catch(() => setNotFound(true));
    api.get(`/blog/${slug}/related`).then(r => setRelated(r.data)).catch(() => {});
  }, [slug]);

  const blocks = useMemo(() => renderMarkdown(post?.content || ""), [post?.content]);
  const toc = useMemo(() => blocks.filter(b => b.type === "h2").map(b => ({ text: b.text, id: b.id })), [blocks]);

  if (notFound) return (
    <div className="min-h-screen" style={{ background: "var(--bg-default)" }}>
      <Navbar />
      <div className="p-24 text-center"><p className="font-mono text-sm">Article introuvable.</p><Link to="/blog" className="underline mt-4 inline-block">← Retour au blog</Link></div>
      <Footer />
    </div>
  );
  if (!post) return <div className="p-24 text-center font-mono text-sm">Chargement...</div>;

  const detectVideoKind = (u) => {
    if (/youtu/.test(u)) return "youtube";
    if (/vimeo/.test(u)) return "vimeo";
    return "upload";
  };
  const videoEmbed = post.video_url ? { kind: detectVideoKind(post.video_url), url: post.video_url } : null;
  const shareUrl = encodeURIComponent(`${window.location.origin}/blog/${post.slug}`);
  const shareTitle = encodeURIComponent(post.title);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-default)" }}>
      <Navbar />
      <article className="max-w-3xl mx-auto px-6 md:px-8 py-16" data-testid="blog-post">
        <Link to="/blog" className="text-xs font-bold uppercase tracking-widest mb-6 inline-flex items-center gap-2" style={{ color: "var(--text-secondary)" }} data-testid="back-to-blog">
          <ArrowLeft className="w-3 h-3" /> Retour au blog
        </Link>
        <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-widest mb-4 mt-6" style={{ color: "var(--text-secondary)" }}>
          <span className="bg-[color:var(--solar)]/20 border border-slate-900 px-2 py-0.5 text-slate-900">{post.category}</span>
          <span className="flex items-center gap-1 font-mono normal-case tracking-normal"><Calendar className="w-3 h-3" />{new Date(post.created_at).toLocaleDateString("fr-FR")}</span>
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight leading-[0.95] mb-6">{post.title}</h1>
        <p className="text-lg leading-relaxed mb-8" style={{ color: "var(--text-secondary)" }}>{post.excerpt}</p>

        {/* Tags */}
        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {post.tags.map(t => (<Link key={t} to={`/blog?q=${encodeURIComponent(t)}`} className="tag hover:bg-[color:var(--solar)]">#{t}</Link>))}
          </div>
        )}

        {post.cover_image_url && (
          <div className="aspect-[16/9] overflow-hidden border border-slate-900/15 mb-8">
            <img src={post.cover_image_url} alt={post.title} loading="eager" className="w-full h-full object-cover" />
          </div>
        )}
        {videoEmbed && (
          <div className="aspect-video mb-8 border border-slate-900/15 bg-black">
            <VideoPlayer video={videoEmbed} className="w-full h-full" controls />
          </div>
        )}

        {/* Table of contents */}
        {toc.length >= 3 && (
          <nav className="bg-[color:var(--bg-surface)] border border-slate-900/15 p-5 mb-10" data-testid="toc">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-3" style={{ color: "var(--text-secondary)" }}><List className="w-3 h-3"/>Sommaire</div>
            <ol className="space-y-1 list-decimal list-inside text-sm">
              {toc.map((t) => <li key={`toc-${t.id}`}><a href={`#${t.id}`} className="hover:underline">{t.text}</a></li>)}
            </ol>
          </nav>
        )}

        {/* Content (markdown) */}
        <div className="prose-content max-w-none">
          {blocks.map((b, i) => {
            if (b.type === "h2") return <h2 key={b.id || `h2-${b.text}`} id={b.id} className="font-display font-bold text-2xl md:text-3xl tracking-tight mt-10 mb-4">{b.text}</h2>;
            if (b.type === "h3") return <h3 key={`h3-${b.text}-${i}`} className="font-display font-bold text-xl mt-8 mb-3">{b.text}</h3>;
            if (b.type === "ul") return <ul key={`ul-${i}`} className="list-disc pl-6 space-y-2 my-4">{b.items.map((it) => <li key={`li-${it}`} dangerouslySetInnerHTML={inline(it)} />)}</ul>;
            return <p key={`p-${i}-${b.text.slice(0,16)}`} className="text-base leading-relaxed my-4" dangerouslySetInnerHTML={inline(b.text)} />;
          })}
        </div>

        {/* Share */}
        <div className="mt-12 pt-6 border-t border-slate-900/10 flex flex-wrap items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--text-secondary)" }}><Share2 className="w-3 h-3 inline mr-1"/>Partager :</span>
          <a target="_blank" rel="noreferrer" href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} className="text-xs font-bold uppercase border border-slate-900 px-3 py-1.5 hover:bg-slate-900 hover:text-white">LinkedIn</a>
          <a target="_blank" rel="noreferrer" href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`} className="text-xs font-bold uppercase border border-slate-900 px-3 py-1.5 hover:bg-slate-900 hover:text-white">X / Twitter</a>
          <a target="_blank" rel="noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} className="text-xs font-bold uppercase border border-slate-900 px-3 py-1.5 hover:bg-slate-900 hover:text-white">Facebook</a>
          <a href={`mailto:?subject=${shareTitle}&body=${shareUrl}`} className="text-xs font-bold uppercase border border-slate-900 px-3 py-1.5 hover:bg-slate-900 hover:text-white">Email</a>
        </div>

        {/* CTA */}
        <div className="mt-12 bg-slate-900 text-white p-8">
          <h3 className="font-display font-bold text-2xl mb-3">Un projet à concrétiser ?</h3>
          <p className="text-slate-300 mb-6">Discutons de votre besoin — local d'activité, projet solaire, simulation de rentabilité.</p>
          <Link to="/contact" className="btn-brutal btn-brutal-solar inline-flex items-center gap-2 px-6 py-3 font-bold uppercase tracking-wider text-sm">Nous contacter <ArrowUpRight className="w-4 h-4"/></Link>
        </div>

        <div className="mt-8 text-xs font-mono" style={{ color: "var(--text-muted)" }}>Par {post.author || "Administrateur"} · {new Date(post.created_at).toLocaleDateString("fr-FR")}</div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-8 py-16 border-t border-slate-900/10">
          <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight mb-8">Articles liés</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {related.map(p => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="group bg-[color:var(--bg-surface)] border border-slate-900/15 card-hover" data-testid={`related-${p.slug}`}>
                {p.cover_image_url && <div className="aspect-[16/10] overflow-hidden"><img src={p.cover_image_url} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform"/></div>}
                <div className="p-4">
                  <div className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: "var(--text-secondary)" }}>{p.category}</div>
                  <div className="font-display font-bold text-sm leading-tight">{p.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
